/**
 * Map error codes and messages to user-friendly descriptions
 * @param {any} error - Error object from API
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(error: any): string {
  // Handle network errors
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }
    if (error.message === 'Network Error') {
      return 'Unable to connect to server. Please check your internet connection.';
    }
    return 'An unexpected error occurred. Please try again.';
  }

  const status = error.response?.status;
  const message = error.response?.data?.error || error.response?.data?.message;

  // Handle specific status codes
  switch (status) {
    case 400:
      // Validation errors
      if (error.response?.data?.details) {
        const details = error.response.data.details;
        if (Array.isArray(details) && details.length > 0) {
          return details[0].msg || 'Invalid input provided.';
        }
      }
      return message || 'Invalid request. Please check your input.';

    case 401:
      return 'Your session has expired. Please log in again.';

    case 403:
      return 'You do not have permission to perform this action.';

    case 404:
      return message || 'The requested resource was not found.';

    case 409:
      // Conflict errors (duplicates)
      if (message?.includes('already exists')) {
        return message;
      }
      return 'This item already exists.';

    case 413:
      return 'File size too large. Please choose a smaller file.';

    case 422:
      return 'Unable to process your request. Please check your input.';

    case 429:
      return 'Too many requests. Please wait a moment and try again.';

    case 500:
      return 'Server error. Please try again later.';

    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again later.';

    default:
      return message || 'An unexpected error occurred.';
  }
}

/**
 * Map specific error messages to user-friendly versions
 * @param {string} message - Original error message
 * @returns {string} User-friendly message
 */
export function mapErrorMessage(message: string): string {
  const errorMap: Record<string, string> = {
    'Category not found': 'The selected category no longer exists.',
    'Subcategory not found': 'The selected subcategory no longer exists.',
    'Story not found': 'This story could not be found.',
    'Slide not found': 'This slide could not be found.',
    'Invalid image format': 'Please upload a valid image file (JPG, PNG, GIF).',
    'Narration is required': 'Please add narration text for this slide.',
    'Image is required': 'Please upload an image for this slide.',
    'Title is required': 'Please enter a title for your story.',
    'Email already registered': 'This email is already in use. Please log in or use a different email.',
    'Invalid credentials': 'Invalid email or password. Please try again.',
    'Token expired': 'Your session has expired. Please log in again.',
    'File upload failed': 'Failed to upload file. Please try again.',
  };

  // Check if we have a specific mapping
  for (const [key, value] of Object.entries(errorMap)) {
    if (message.toLowerCase().includes(key.toLowerCase())) {
      return value;
    }
  }

  return message;
}